import { useEffect, useState } from 'react';
import { useOrganization, useUser } from '@clerk/clerk-react';

import myShopClient from '@services/nearlive';

/**
 * Reads + writes the per-user `emailWeeklyLiaUpdate` flag stored on the
 * Clerk organisation-membership's `publicMetadata`. The flag drives the
 * weekly insights digest (Settings → Account → Notifications). Hoisted out
 * of `Notifications.jsx` so the AccountLanding signpost card can read the
 * same state without duplicating the load logic.
 *
 * Returns `{ enabled, loading, save }`:
 *   - `enabled` is `null` until the membership lookup resolves
 *   - `save(next)` POSTs to `/api/email-preferences` then mirrors locally
 */
export function useWeeklyInsightsPreference() {
	const { user } = useUser();
	const { organization } = useOrganization();
	const [enabled, setEnabled] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		if (!user || !organization) return undefined;
		async function load() {
			try {
				const { data: memberships } = await organization.getMemberships();
				const mine = memberships.find((m) => m.publicUserData?.userId === user.id);
				if (!cancelled) setEnabled(!!mine?.publicMetadata?.emailWeeklyLiaUpdate);
			} finally {
				if (!cancelled) setLoading(false);
			}
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [user, organization]);

	const save = async (next) => {
		const client = await myShopClient();
		await client.post('/api/email-preferences', { userId: user.id, emailWeeklyLiaUpdate: next });
		setEnabled(next);
	};

	return { enabled, loading, save };
}
